import { AuditTab } from '@/components/ui/audit-tab';
import { Tabs, TabsContent, TabsList, TabLink } from '@/components/ui/tabs';
import {
	Activity,
	FileText,
	History,
	Receipt
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { ParkingSessionLedger } from './parking-session-ledger';
import { ParkingSessionDetails } from './parking-session-details';
import { ParkingSessionJourneyLogs } from './parking-session-journey-logs';
import { ParkingSessionServices } from './parking-session-services';
import type { ParkingSessionDetail } from './types';

interface ParkingSessionDetailsTabsProps {
	session: ParkingSessionDetail;
	activeTab: string;
	onTabChange: (value: string) => void;
}

export const ParkingSessionDetailsTabs = ({
	session,
	activeTab,
	onTabChange
}: ParkingSessionDetailsTabsProps) => {
	const { t } = useTranslation('parkingSessions');

	return (
		<Tabs
			value={activeTab}
			onValueChange={onTabChange}
			className="flex flex-col h-full min-h-0"
		>
			{/* Tab Bar - fixed height at top */}
			<div className="flex-shrink-0 pb-2">
				<TabsList>
					<TabLink value="details" icon={FileText} label={t('tabs.details')} />
					<TabLink value="ledger" icon={Receipt} label={t('tabs.ledger')} />
					<TabLink value="events" icon={Activity} label={t('tabs.events')} />
					<TabLink value="audit" icon={History} label={t('tabs.audit')} align="right" />
				</TabsList>
			</div>

			{/* Tab Content - scrollable area with min-height for stable modal sizing */}
			<div className="flex-1 min-h-[350px] overflow-y-auto overflow-x-hidden">
				<TabsContent value="details" className="mt-0">
					<ParkingSessionDetails session={session} />
				</TabsContent>
				<TabsContent value="events" className="mt-0">
					<ParkingSessionJourneyLogs
						sessionId={session.parkingSessionId}
						isActive={activeTab === 'events'}
					/>
				</TabsContent>
				<TabsContent value="ledger" className="mt-0">
					<ParkingSessionLedger
						sessionId={session.parkingSessionId}
						isActive={activeTab === 'ledger'}
					/>
				</TabsContent>
				<TabsContent value="services" className="mt-0">
					<ParkingSessionServices
						sessionId={session.parkingSessionId}
					/>
				</TabsContent>
				<TabsContent value="audit" className="mt-0">
					<AuditTab
						data={{
							createdAt: session.entryDateTime,
							updatedAt: session.exitDateTime
						}}
					/>
				</TabsContent>
			</div>
		</Tabs>
	);
};
